import { SystemLog, LogFilterOptions } from "./types";
import { getClientSession } from "@/services/auth/clientSession";
import { monitoringMicroservice } from "@/services/monitoring/microservice";
import { isSystemProxyStubResponse } from "./apiMonitoringService";
import { API_ENDPOINTS } from "@/config/api";

const LOG_DEDUPE_WINDOW_MS = 30 * 1000;
const LOG_MAX_EVENTS_PER_MINUTE = 20;

type DedupeState = {
  windowStartMs: number;
  sentThisWindow: number;
  lastSentByKey: Map<string, number>;
};

const dedupeState: DedupeState = {
  windowStartMs: Date.now(),
  sentThisWindow: 0,
  lastSentByKey: new Map(),
};

export const __resetLogDedupeForTests = () => {
  dedupeState.windowStartMs = Date.now();
  dedupeState.sentThisWindow = 0;
  dedupeState.lastSentByKey.clear();
};

function buildDedupeKey(logData: Partial<SystemLog>, userId?: string) {
  return [
    String(logData.level || "info"),
    String(logData.component || "system"),
    String(logData.service || "frontend"),
    String(logData.message || ""),
    userId || "",
  ].join("|");
}

/**
 * Should Send Log.
 */
function shouldSendLog(key: string) {
  const now = Date.now();

  // reset minute window
  if (now - dedupeState.windowStartMs >= 60 * 1000) {
    dedupeState.windowStartMs = now;
    dedupeState.sentThisWindow = 0;
  }

  // hard cap (prevents storms)
  if (dedupeState.sentThisWindow >= LOG_MAX_EVENTS_PER_MINUTE) {
    return false;
  }

  // de-dupe repeated identical logs in short window
  const last = dedupeState.lastSentByKey.get(key);
  if (last && now - last < LOG_DEDUPE_WINDOW_MS) {
    return false;
  }

  dedupeState.lastSentByKey.set(key, now);
  dedupeState.sentThisWindow += 1;

  // best-effort pruning
  if (dedupeState.lastSentByKey.size > 2000) {
    dedupeState.lastSentByKey.clear();
  }

  return true;
}

/**
 * Fetches system logs from database with applied filters
 */
function mapSystemLogRow(row: Record<string, unknown>): SystemLog {
  const meta = (row.metadata ?? row.context ?? row.details ?? {}) as Record<string, unknown>;
  const metaUser = meta.user as Record<string, unknown> | undefined;

  return {
    id: typeof row.id === "string" || typeof row.id === "number" ? String(row.id) : "",
    timestamp: (row.timestamp as string) || (row.created_at as string),
    level: row.level as string,
    component: row.component as string,
    message: row.message as string,
    service: row.service as string | undefined,
    userId:
      (row.user_id as string | undefined) ||
      (row.userId as string | undefined) ||
      (meta.userId as string | undefined) ||
      (metaUser?.id as string | undefined),
    userEmail:
      (row.user_email as string | undefined) ||
      (row.userEmail as string | undefined) ||
      (meta.userEmail as string | undefined) ||
      (metaUser?.email as string | undefined),
    context: meta,
    createdAt: (row.created_at as string) || (row.timestamp as string),
  } as SystemLog;
}

/**
 * Fetch System Logs.
 */
export const fetchSystemLogs = async (
  limit: number = 200,
  filters?: LogFilterOptions
): Promise<SystemLog[]> => {
  const { data: sessionData } = await getClientSession();
  if (!sessionData.session) {
    throw new Error("No active session");
  }

  const queryParams = new URLSearchParams();
  queryParams.append("limit", limit.toString());

  if (filters?.level) {
    queryParams.append("level", filters.level);
  }
  if (filters?.component) {
    queryParams.append("component", filters.component);
  }
  if (filters?.timeRange) {
    queryParams.append("timeRange", filters.timeRange);
  }
  if (filters?.search) {
    queryParams.append("search", filters.search);
  }

  const response = await fetch(`/api/system/logs?${queryParams.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionData.session.access_token}`,
    },
    credentials: "include",
  });

  let result: unknown;
  try {
    result = await response.json();
  } catch (parseError) {
    console.error("Error parsing system logs response:", parseError);
    throw new Error("Failed to parse system logs response");
  }

  if (!response.ok || isSystemProxyStubResponse(result)) {
    const resultObj = result as { error?: string; message?: string } | null;
    throw new Error(resultObj?.error || resultObj?.message || "Failed to fetch system logs");
  }

  const rows = Array.isArray(result) ? (result as Array<Record<string, unknown>>) : [];
  return rows.map(mapSystemLogRow);
};

/**
 * Read Session User For Logging.
 */
async function readSessionUserForLogging(): Promise<{
  userId?: string;
  userEmail?: string;
  userName?: string;
}> {
  try {
    const { data: session } = await getClientSession();
    if (!session?.session?.user) {
      return {};
    }

    const metaRaw = session.session.user.user_metadata;
    const meta = metaRaw && typeof metaRaw === "object" ? (metaRaw as Record<string, unknown>) : {};
    const userName =
      (typeof meta.name === "string" && meta.name) ||
      (typeof meta.full_name === "string" && meta.full_name) ||
      (typeof meta.fullName === "string" && meta.fullName) ||
      (typeof meta.username === "string" && meta.username) ||
      undefined;

    return {
      userId: session.session.user.id,
      userEmail: session.session.user.email,
      userName,
    };
  } catch (err) {
    console.warn("Could not get session for system logging:", err);
    return {};
  }
}

/**
 * Should Skip Non Admin Log.
 */
async function shouldSkipNonAdminLog(
  logData: Partial<SystemLog>,
  userId: string | undefined
): Promise<boolean> {
  const isCriticalError =
    logData.level === "error" || logData.level === "fatal" || logData.level === "warn";
  if (isCriticalError || !userId) {
    return false;
  }

  const { isAdminOrSuperAdmin } = await import("@/utils/adminCheck");
  const isAdmin = await isAdminOrSuperAdmin();
  return !isAdmin;
}

type SystemLogLevel = "debug" | "info" | "warn" | "error";

function normalizeLogLevel(level: unknown): SystemLogLevel {
  const normalizedLevel = (typeof level === "string" ? level : "info").toLowerCase();
  const allowedLevels = new Set<SystemLogLevel>(["debug", "info", "warn", "error"]);
  return allowedLevels.has(normalizedLevel as SystemLogLevel)
    ? (normalizedLevel as SystemLogLevel)
    : "info";
}

/**
 * Logs a system event to database
 * CRITICAL: Only logs for admin/super admin users (except critical errors)
 */
export const logSystemEvent = async (logData: Partial<SystemLog>): Promise<void> => {
  try {
    const { userId, userEmail, userName } = await readSessionUserForLogging();

    if (await shouldSkipNonAdminLog(logData, userId)) {
      return;
    }

    const key = buildDedupeKey(logData, userId);
    if (!shouldSendLog(key)) {
      return;
    }

    const context = logData.context && typeof logData.context === "object" ? logData.context : {};
    const enrichedMetadata = {
      ...context,
      userId,
      userEmail,
      userName,
      origin: typeof window !== "undefined" ? window.location.origin : undefined,
    };

    await monitoringMicroservice.logSystemEvent({
      level: normalizeLogLevel(logData.level),
      component: logData.component || "system",
      message: logData.message || "",
      service: logData.service || "frontend",
      user_id: userId,
      metadata: enrichedMetadata,
    });
  } catch (error) {
    console.warn("Error in logSystemEvent (non-critical):", error);
  }
};

/**
 * Purge old system logs
 */
export const purgeSystemLogs = async (daysToKeep: number = 30): Promise<number> => {
  try {
    const { authService } = await import("@/services/auth/authService");
    const headers = await authService.getAuthHeaders();
    const response = await fetch(API_ENDPOINTS.SYSTEM.LOGS_PURGE, {
      method: "POST",
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ daysToKeep }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const payload = (await response.json()) as { deletedCount?: number };
    const deletedCount = payload.deletedCount || 0;

    // Log the purge action
    await logSystemEvent({
      level: "info",
      component: "system",
      message: "System logs purged",
      service: "admin",
      context: {
        action: "purge_logs",
        days_kept: daysToKeep,
        deleted_count: deletedCount,
      },
    });

    return deletedCount;
  } catch (error) {
    console.error("Error purging system logs:", error);
    throw error;
  }
};
